import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { clampPositions, handleAnimation, } from '@polestar/component-warehouse-react/components/range-selector/RangeSelector.helpers';
import { css } from '@emotion/react';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { createCSSTransition, createTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { clamp, getClosest, percentageToValue, toPrecision, valueToPercentage, } from '@polestar/component-warehouse-react/helpers/mathematical';
import { m as motion, useAnimationControls } from 'framer-motion';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import HiddenAccessibilityElement from '@polestar/component-warehouse-react/components/hidden-accessibility-element/HiddenAccessibilityElement';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { useContainerFocusTriggers } from '@polestar/component-warehouse-react/hooks/useContainerFocusTriggers';
import { focusProviderSkipProps } from '@polestar/component-warehouse-react/contexts/focus';
import { focusWithinStyle } from '@polestar/component-warehouse-react/helpers/accessibility';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
export var RangeSelectorPosition;
(function (RangeSelectorPosition) {
    RangeSelectorPosition["min"] = "min";
    RangeSelectorPosition["max"] = "max";
})(RangeSelectorPosition || (RangeSelectorPosition = {}));
const handleSize = 48;
const dotSize = 10;
const RangeSelector = forwardRef(({ id, value = [10, 90], onChange, onInput, onBlur, onFocus, min = 0, max = 100, steps = 0, disabled = false, error = false, singleValue = false, orientation = Orientations.horizontal, animate = true, animateTransition, handleProps, minHandleProps, maxHandleProps, ['aria-labelledby']: ariaLabelledBy, ...props }, ref) => {
    const memoizedId = useSSRSafeId('range-selector-', id);
    /**
     * Refs
     */
    const handleMin = useRef(null);
    const handleMax = useRef(null);
    const track = useRef(null);
    const [internalValue, setInternalValue] = useState(null);
    const keyTimeout = useRef();
    const [minValue, maxValue] = internalValue !== null && internalValue !== void 0 ? internalValue : value;
    const [isDragging, setIsDragging] = useState(false);
    const hasHorizontalOrientation = orientation === Orientations.horizontal;
    const orientationAxis = hasHorizontalOrientation ? 'X' : 'Y';
    const positionMinRef = useRef(0);
    const positionMaxRef = useRef(0);
    positionMinRef.current = valueToPercentage(minValue, min, max);
    positionMaxRef.current = valueToPercentage(maxValue, min, max);
    const railPercentage = clamp(positionMaxRef.current - positionMinRef.current || 0, 0, 1);
    const { isRTL } = useDirection();
    // set snap positions
    const snapPositions = useMemo(() => {
        if (!steps) {
            return null;
        }
        const snapPositions = [];
        if (steps) {
            for (let i = 0; i < steps; i++) {
                snapPositions[i] = (1 / (steps - 1)) * i;
            }
        }
        return snapPositions;
    }, [steps]);
    /**
     * Fire callback to communicate the new value from the slider
     */
    const triggerUpdateHandler = useCallback(([minPosition, maxPosition]) => {
        const values = [
            toPrecision(clamp(percentageToValue(minPosition, min, max), min, max), 7),
            toPrecision(clamp(percentageToValue(maxPosition, min, max), min, max), 7),
        ];
        onInput === null || onInput === void 0 ? void 0 : onInput(values);
        setInternalValue(values);
    }, [min, max, onInput]);
    const triggerChangeHandler = useCallback(([minPosition, maxPosition]) => {
        onChange === null || onChange === void 0 ? void 0 : onChange([
            toPrecision(clamp(percentageToValue(minPosition, min, max), min, max), 7),
            toPrecision(clamp(percentageToValue(maxPosition, min, max), min, max), 7),
        ]);
    }, [min, max, onChange]);
    useEffect(() => {
        if (isDragging) {
            keyTimeout.current && clearTimeout(keyTimeout.current);
        }
        else if (internalValue) {
            triggerChangeHandler([positionMinRef.current, positionMaxRef.current]);
            setInternalValue(null);
        }
    }, [internalValue, isDragging, triggerChangeHandler]);
    /**
     * Setup gesture events handler
     */
    const clampPosition = useCallback((position) => clamp(snapPositions ? getClosest(snapPositions, position) : position, 0, 1), [snapPositions]);
    const handleKeyboardControls = (position) => (event) => {
        setIsDragging(true);
        let newPosition = null;
        const target = event.target;
        const { clientWidth: width, clientHeight: height } = target.parentElement;
        const length = hasHorizontalOrientation ? width : height;
        const startPosition = position === RangeSelectorPosition.min ? positionMinRef.current : positionMaxRef.current;
        const divider = steps ? steps - 1 : length;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                newPosition = startPosition + 1 / divider;
                break;
            case 'PageUp':
                newPosition = startPosition + (steps ? 1 / divider : 0.1);
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                newPosition = startPosition - 1 / divider;
                break;
            case 'PageDown':
                newPosition = startPosition - (steps ? 1 / divider : 0.1);
                break;
        }
        if (newPosition !== null) {
            preventCancelableDefault(event);
            const positions = clampPositions(position, position === RangeSelectorPosition.min
                ? toPrecision(newPosition, 7)
                : positionMinRef.current, position === RangeSelectorPosition.max
                ? toPrecision(newPosition, 7)
                : positionMaxRef.current);
            triggerUpdateHandler(positions);
        }
    };
    const handleKeyUpEvent = useCallback(() => {
        // prevent duplicate values with onChange events
        keyTimeout.current = setTimeout(() => {
            setIsDragging(false);
        }, 100);
    }, [keyTimeout]);
    const [trackPosition, setTrackPosition] = useState({
        position: 0,
        spacing: 0,
    });
    const dragHandle = (position) => (event, info) => {
        const pixelPosition = Math.max(0, Math.min(trackPosition.spacing, info.point[hasHorizontalOrientation ? 'x' : 'y'] - trackPosition.position));
        const newPosition = !hasHorizontalOrientation || isRTL
            ? 1 - clampPosition(pixelPosition / trackPosition.spacing)
            : clampPosition(pixelPosition / trackPosition.spacing);
        const newPositions = clampPositions(position, position === RangeSelectorPosition.min ? newPosition : positionMinRef.current, position === RangeSelectorPosition.max ? newPosition : positionMaxRef.current);
        // Prevent unnecessary update triggers (makes it slow on mobile)
        // We get a .000000000001 floating point difference sometimes, so can't do a direct compare
        if (Math.abs(newPositions[0] - positionMinRef.current) > 0.00001 ||
            Math.abs(newPositions[1] - positionMaxRef.current) > 0.00001) {
            triggerUpdateHandler(newPositions);
        }
    };
    const minDragHandle = dragHandle(RangeSelectorPosition.min);
    const maxDragHandle = dragHandle(RangeSelectorPosition.max);
    const minKeyControls = handleKeyboardControls(RangeSelectorPosition.min);
    const maxKeyControls = handleKeyboardControls(RangeSelectorPosition.max);
    const handlePanEnd = useCallback(() => {
        setIsDragging(false);
    }, []);
    const handlePanStart = useCallback((e) => {
        var _a, _b, _c, _d, _e, _f, _g;
        // prevent unnecessary blur events
        preventCancelableDefault(e);
        // focus on the input field to ensure focus events are captured
        (e === null || e === void 0 ? void 0 : e.target) === ((_a = handleMin.current) === null || _a === void 0 ? void 0 : _a.parentNode)
            ? (_b = handleMin.current) === null || _b === void 0 ? void 0 : _b.focus()
            : (e === null || e === void 0 ? void 0 : e.target) === ((_c = handleMax.current) === null || _c === void 0 ? void 0 : _c.parentNode)
                ? (_d = handleMax.current) === null || _d === void 0 ? void 0 : _d.focus()
                : null;
        setIsDragging(true);
        const trackRect = ((_e = track === null || track === void 0 ? void 0 : track.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect()) || { left: 0, right: 0, top: 0 };
        setTrackPosition({
            position: (hasHorizontalOrientation
                ? trackRect.left + dotSize / 2 + window.scrollX
                : trackRect.top + dotSize / 2 + window.scrollY) || 0,
            spacing: Math.max(0, ((hasHorizontalOrientation
                ? (_f = track.current) === null || _f === void 0 ? void 0 : _f.clientWidth
                : (_g = track.current) === null || _g === void 0 ? void 0 : _g.clientHeight) || 0) - dotSize),
        });
    }, [hasHorizontalOrientation]);
    const minEventHandleProps = disabled
        ? { ...minHandleProps, ...handleProps }
        : {
            onKeyDown: minKeyControls,
            onKeyUp: handleKeyUpEvent,
            onPan: minDragHandle,
            onMouseDown: handlePanStart,
            onTouchStart: handlePanStart,
            onPanEnd: handlePanEnd,
            ...minHandleProps,
            ...handleProps,
        };
    const maxEventHandleProps = disabled
        ? { ...maxHandleProps, ...handleProps }
        : {
            onPan: maxDragHandle,
            onKeyDown: maxKeyControls,
            onKeyUp: handleKeyUpEvent,
            onMouseDown: handlePanStart,
            onTouchStart: handlePanStart,
            onPanEnd: handlePanEnd,
            ...maxHandleProps,
            ...handleProps,
        };
    /**
     * In order to decide which handle to move, we need to take 3 variables in consideration:
     * 1. singleValue, is this a slider? true -> move max handle
     * 2. closestPosition, when the handles don't have the same position,
     *    we do want to move the handle where the user clicked closed to
     * 3. samePosition, when the handles do have the same position,
     *    we want to move the min handle when the user clicks on the left hand side,
     *    and right when the user clicked on the right hand side
     */
    const onSliderClick = useCallback((event) => {
        var _a, _b;
        const { clientWidth: width, clientHeight: height } = event.nativeEvent
            .target;
        const xPosition = (event.nativeEvent.offsetX - dotSize / 2) / (width - dotSize);
        const yPosition = (event.nativeEvent.offsetY - dotSize / 2) / (height - dotSize);
        const unclampedPosition = hasHorizontalOrientation
            ? isRTL
                ? 1 - xPosition
                : xPosition
            : 1 - yPosition;
        const closestHandlerPosition = getClosest([positionMinRef.current, positionMaxRef.current], unclampedPosition);
        const newPosition = clampPosition(unclampedPosition);
        const samePosition = positionMinRef.current === positionMaxRef.current;
        const closestIsMin = closestHandlerPosition === positionMinRef.current;
        const moveMin = !singleValue &&
            ((!samePosition && closestIsMin) ||
                (samePosition && closestIsMin && newPosition < positionMinRef.current));
        // focus on the input field to ensure focus events are captured
        moveMin ? (_a = handleMin.current) === null || _a === void 0 ? void 0 : _a.focus() : (_b = handleMax.current) === null || _b === void 0 ? void 0 : _b.focus();
        const newPositions = [
            moveMin ? newPosition : positionMinRef.current,
            !moveMin ? newPosition : positionMaxRef.current,
        ];
        triggerUpdateHandler(newPositions);
        setInternalValue(null);
        triggerChangeHandler(newPositions);
    }, [
        handleMin,
        handleMax,
        hasHorizontalOrientation,
        clampPosition,
        singleValue,
        triggerUpdateHandler,
        triggerChangeHandler,
        isRTL,
    ]);
    const transition = animateTransition || createTransition({ disabled: isDragging || !animate });
    const sharedHandleProps = {
        error: singleValue && error,
        ...aria({
            valuemin: min,
            valuemax: max,
            orientation: orientation,
            role: 'slider',
            labelledby: ariaLabelledBy,
        }),
        disabled,
        hasHorizontalOrientation,
        initial: false,
        transition,
    };
    const { focusBlurEvents } = useContainerFocusTriggers({
        onFocus,
        onBlur,
    });
    /**
     * Define and manage animation controls
     */
    const railAnimation = useAnimationControls();
    const minHandleAnimation = useAnimationControls();
    const maxHandleAnimation = useAnimationControls();
    const getRailTarget = useCallback((positionMin) => ({
        [`translate${orientationAxis}`]: `${isRTL && hasHorizontalOrientation ? 100 - positionMin * 100 : positionMin * 100}%`,
        [`scale${orientationAxis}`]: isRTL && hasHorizontalOrientation ? -railPercentage : railPercentage,
    }), [hasHorizontalOrientation, isRTL, orientationAxis, railPercentage]);
    const getMinHandleTarget = useCallback((positionMin) => hasHorizontalOrientation
        ? {
            [isRTL ? 'right' : 'left']: `${positionMin * 100}%`,
        }
        : {
            bottom: `${positionMin * 100}%`,
        }, [hasHorizontalOrientation, isRTL]);
    const getMaxHandleTarget = useCallback((positionMax) => hasHorizontalOrientation
        ? {
            [isRTL ? 'right' : 'left']: `${positionMax * 100}%`,
        }
        : {
            bottom: `${positionMax * 100}%`,
        }, [hasHorizontalOrientation, isRTL]);
    useEffect(() => {
        const animations = [
            {
                animation: railAnimation,
                target: getRailTarget(positionMinRef.current),
            },
            {
                animation: minHandleAnimation,
                target: getMinHandleTarget(positionMinRef.current),
            },
            {
                animation: maxHandleAnimation,
                target: getMaxHandleTarget(positionMaxRef.current),
            },
        ];
        animations.forEach(({ animation, target }) => {
            handleAnimation(animation, target, {
                isDragging,
                animate,
                hasAnimationTransition: !!animateTransition,
            });
        });
    }, [
        animate,
        animateTransition,
        value,
        isDragging,
        railAnimation,
        minHandleAnimation,
        maxHandleAnimation,
        getMaxHandleTarget,
        getRailTarget,
        getMinHandleTarget,
    ]);
    return (React.createElement(Container, { ...props, id: memoizedId, ref: ref, hasHorizontalOrientation: hasHorizontalOrientation, ...focusBlurEvents },
        React.createElement(Track, { ref: track, disabled: disabled, hasHorizontalOrientation: hasHorizontalOrientation },
            React.createElement(Rail, { error: singleValue && error, initial: getRailTarget(positionMinRef.current), animate: railAnimation, transition: transition, disabled: disabled, hasHorizontalOrientation: hasHorizontalOrientation })),
        React.createElement(ClickArea, { onClick: disabled ? undefined : onSliderClick, "data-type": "range-selector-click-area" }),
        React.createElement(HandleWrapper, { hasHorizontalOrientation: hasHorizontalOrientation },
            React.createElement(HandleOffset, null,
                !singleValue && (React.createElement(Handle, { id: `${memoizedId}-handle-min`, inputRef: handleMin, ...sharedHandleProps, ...aria({
                        valuenow: Math.floor(minValue),
                    }), ...minEventHandleProps, animate: minHandleAnimation, initial: getMinHandleTarget(positionMinRef.current) })),
                React.createElement(Handle, { id: `${memoizedId}-handle-max`, inputRef: handleMax, ...sharedHandleProps, ...aria({
                        valuenow: Math.floor(maxValue),
                    }), ...maxEventHandleProps, animate: maxHandleAnimation, initial: getMaxHandleTarget(positionMaxRef.current) })))));
});
RangeSelector.displayName = 'RangeSelector';
export default RangeSelector;
const Handle = forwardRef(({ error, tabIndex, inputRef, 'aria-labelledby': ariaLabelledBy, 'aria-valuemin': ariaValueMin, 'aria-valuemax': ariaValueMax, 'aria-valuenow': ariaValueNow, 'aria-valuetext': ariaValueText, 'aria-orientation': ariaOrientation, 'aria-label': ariaLabel, role, ...props }, ref) => (React.createElement(HandleContainer, { ...props, ref: ref },
    !props.disabled && React.createElement(HandleCircle, null),
    React.createElement(HiddenAccessibilityElement, { as: "input", type: "range", ref: inputRef, min: ariaValueMin, max: ariaValueMax, tabIndex: tabIndex, defaultValue: ariaValueNow, ...aria({
            label: ariaLabel,
            labelledby: ariaLabelledBy,
            role,
            orientation: ariaOrientation,
            valuetext: ariaValueText,
        }), ...focusProviderSkipProps }),
    React.createElement(HandleDot, { disabled: props.disabled, error: error }))));
Handle.displayName = 'Handle';
const Container = styled('div', shouldNotForwardProps(true, 'hasHorizontalOrientation'))(({ hasHorizontalOrientation }) => css `
    width: ${hasHorizontalOrientation ? '100%' : `${handleSize}px`};
    height: ${hasHorizontalOrientation ? `${handleSize}px` : '100%'};
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
  `);
const ClickArea = styled.div `
  width: 100%;
  height: 100%;
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
`;
const Track = styled('div', shouldNotForwardProps(true, 'hasHorizontalOrientation'))(({ theme, hasHorizontalOrientation }) => css `
    width: ${hasHorizontalOrientation ? '100%' : '2px'};
    height: ${hasHorizontalOrientation ? '2px' : '100%'};
    background: ${theme.slider.trackColor};
    position: relative;
  `);
const Rail = styled(motion.div, shouldNotForwardProps(false, 'disabled', 'hasHorizontalOrientation', 'error'))(({ theme, disabled, hasHorizontalOrientation, error }) => css(css `
      width: ${hasHorizontalOrientation ? '100%' : '2px'};
      height: ${hasHorizontalOrientation ? '2px' : '100%'};
      background: ${theme.slider[disabled ? 'disabledColor' : 'railColor']};
      position: absolute;
      inset-inline-start: 0;
      inset-block-end: 0;
      transform: scaleX(0);
      transform-origin: 0 ${hasHorizontalOrientation ? 0 : '100%'};
    `, error &&
    css `
        background-color: ${theme.slider.errorColor};
      `));
const HandleWrapper = styled.div(({ hasHorizontalOrientation }) => css(css `
        position: absolute;
        pointer-events: none;
      `, hasHorizontalOrientation &&
    css `
          inset-inline-start: ${dotSize / 2}px;
          inset-inline-end: ${dotSize / 2}px;
          height: 100%;
        `, !hasHorizontalOrientation &&
    css `
          inset-block-start: 5px;
          inset-block-end: 5px;
          width: 100%;
        `));
const HandleOffset = styled.div `
  position: relative;
  width: 100%;
  height: 100%;
`;
const HandleContainer = styled(motion.div, shouldNotForwardProps(false, 'disabled', 'hasHorizontalOrientation'))(({ theme, disabled, hasHorizontalOrientation }) => {
    return css(css `
      pointer-events: auto;
      -webkit-appearance: none;
      box-shadow: none;
      outline: none;
      width: ${handleSize}px;
      height: ${handleSize}px;
      background: none;
      border-radius: 50%;
      position: absolute;
      inset-inline-start: 0;
      padding: 0;
      border: none;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: none;

      ${focusWithinStyle({ theme })};
    `, hasHorizontalOrientation &&
        css `
        margin-inline-start: ${-handleSize / 2}px;
        inset-block-start: 50%;
        transform: translateY(-50%);
      `, !hasHorizontalOrientation &&
        css `
        margin-block-end: ${-handleSize / 2}px;
        inset-inline-start: 50%;
        transform: translateX(-50%);
      `, !disabled &&
        css `
        &:hover {
          @media (pointer: coarse) {
            div:first-of-type {
              transform: scale(3.2);
            }
          }
        }
      `, !disabled &&
        css `
        cursor: grab;

        div:first-of-type {
          transition: ${createCSSTransition('transform', MotionEasings.easeOut, MotionDurations.fast)};
          transform: scale(1);
        }

        &:active {
          cursor: grabbing;

          div:first-of-type {
            transform: scale(2.4);
          }
        }
      `);
});
const HandleCircle = styled.div `
  width: ${dotSize}px;
  height: ${dotSize}px;
  background-color: ${({ theme }) => theme.slider.handleCircleColor};
  transform-origin: center center;
  border-radius: 50%;
  inset-block-start: 50%;
  inset-inline-start: 50%;
  position: absolute;
  margin: -${dotSize / 2}px;
  pointer-events: none;
`;
const HandleDot = styled(HandleCircle, shouldNotForwardProps(false, 'disabled', 'hasHorizontalOrientation', 'error'))(({ theme, disabled, error }) => css(css `
      background-color: ${theme.slider[disabled ? 'disabledColor' : 'handleDotColor']};
    `, error &&
    css `
        background-color: ${theme.slider.errorColor};
      `));
